import React, { createContext, useContext, useState } from 'react';
import { es } from './locales/es';
import { en } from './locales/en';

type Language = 'es' | 'en';

type Translations = {
  [key: string]: string;
};

const dictionaries: Record<Language, Translations> = {
  es,
  en,
};

interface I18nContextType {
  t: (key: string, params?: Record<string, string | number>) => string;
  language: Language;
  setLanguage: (lang: Language) => void;
}

const I18nContext = createContext<I18nContextType | null>(null);

/**
 * Interpolate parameters into a translation string
 * Supports both {param} and {{param}} syntax
 */
function interpolate(text: string, params?: Record<string, string | number>): string {
  if (!params) return text;

  let result = text;
  for (const [key, value] of Object.entries(params)) {
    // Replace both {key} and {{key}} patterns
    result = result.replace(new RegExp(`\\{\\{?${key}\\}?\\}`, 'g'), String(value));
  }
  return result;
}

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [language, setLanguage] = useState<Language>('es');

  const t = (key: string, params?: Record<string, string | number>): string => {
    const translation = dictionaries[language][key] || key;
    return interpolate(translation, params);
  };

  return React.createElement(
    I18nContext.Provider,
    { value: { t, language, setLanguage } },
    children
  );
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};

// Roles del sistema con traducción propia (roles.ADMIN, roles.RESIDENT, ...).
// Un rol personalizado (creado en Admin > Roles) no tiene entrada en el
// diccionario — t() no encuentra la key y regresa la key tal cual
// ("roles.MiRolPersonalizado"), así que hay que mostrar el nombre crudo
// en vez de pasarlo por t().
const SYSTEM_ROLES = new Set(['ADMIN', 'SECTION_ADMIN', 'RESIDENT', 'GUARD', 'STAFF', 'TENANT_USER', 'FAMILIAR']);

/** Nombre a mostrar para un rol: traducido si es del sistema, tal cual si es personalizado. */
export const getRoleLabel = (role: string | undefined | null, t: (key: string) => string): string => {
  if (!role) return '';
  return SYSTEM_ROLES.has(role.toUpperCase()) ? t(`roles.${role}`) : role;
};
